/**
 * Feb 2026 R293zp · Compact searchable multi-select filter popover.
 *
 * Replaces the inline chip bar on the Sales list with a dense toolbar
 * trigger that opens a Command (cmdk) palette: search box on top,
 * scrollable checkbox list below with per-value counts, and a
 * "Clear" footer. Applied count surfaces on the trigger label so the
 * user knows at a glance how many values are active without opening.
 *
 * Props:
 *   label          — plain label for the trigger ("Status", "Customer")
 *   icon           — optional lucide icon
 *   options        — [{ value, label?, count }]
 *   selected       — Set of currently applied values
 *   onToggle(val)  — toggle a single value
 *   onClear()      — clear every applied value
 *   align          — Popover align (default "start")
 *   placeholder    — search box placeholder
 *   maxHeight      — max body height class (default max-h-64)
 */
import { useMemo, useState } from "react";
import {
  Popover, PopoverTrigger, PopoverContent,
} from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { Check, ChevronDown, Search, X } from "lucide-react";

export default function MultiSelectFilter({
  label, icon: Icon, options, selected, onToggle, onClear,
  align = "start", placeholder = "Search…", maxHeight = "max-h-64",
  testid,
}) {
  const [q, setQ] = useState("");
  const active = selected.size;
  const filtered = useMemo(() => {
    if (!q) return options;
    const needle = q.toLowerCase();
    return options.filter((o) =>
      String(o.label ?? o.value).toLowerCase().includes(needle) ||
      String(o.value).toLowerCase().includes(needle));
  }, [options, q]);

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className={
            "h-8 px-2.5 gap-1.5 text-xs border-border " +
            (active ? "border-foreground bg-foreground/5 text-foreground" : "")
          }
          data-testid={testid}
        >
          {Icon && <Icon className="h-3.5 w-3.5" />}
          {label}
          {active > 0 && (
            <span className="h-4 min-w-4 px-1 rounded-full bg-foreground text-background text-[10px] font-bold inline-flex items-center justify-center tabular-nums">
              {active}
            </span>
          )}
          <ChevronDown className="h-3 w-3 opacity-70" />
        </Button>
      </PopoverTrigger>
      <PopoverContent align={align} className="w-72 p-0 overflow-hidden" sideOffset={6}>
        <div className="relative border-b border-border">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
          <input
            type="text"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={placeholder}
            className="w-full h-9 pl-8 pr-3 text-xs bg-transparent outline-none"
            data-testid={testid ? `${testid}-search` : undefined}
          />
        </div>
        <div className={"overflow-y-auto " + maxHeight}>
          {filtered.length === 0 && (
            <div className="px-3 py-6 text-center text-[11px] text-muted-foreground">
              Nothing to filter
            </div>
          )}
          {filtered.map((o) => {
            const isOn = selected.has(o.value);
            return (
              <button
                key={o.value}
                type="button"
                onClick={() => onToggle(o.value)}
                className={
                  "w-full flex items-center gap-2 px-2.5 py-1.5 text-xs text-left hover:bg-muted transition " +
                  (isOn ? "bg-primary/5" : "")
                }
                data-testid={testid ? `${testid}-opt-${String(o.value).replace(/\s+/g, "_")}` : undefined}
              >
                <span
                  className={
                    "h-4 w-4 shrink-0 rounded border inline-flex items-center justify-center " +
                    (isOn ? "bg-foreground border-foreground text-background" : "border-border")
                  }
                >
                  {isOn && <Check className="h-3 w-3" />}
                </span>
                <span className="flex-1 truncate" title={String(o.label ?? o.value)}>
                  {o.label ?? o.value}
                </span>
                {typeof o.count === "number" && (
                  <span className="text-[10px] text-muted-foreground tabular-nums">
                    {o.count}
                  </span>
                )}
              </button>
            );
          })}
        </div>
        {active > 0 && (
          <div className="border-t border-border p-1.5 flex items-center justify-between">
            <span className="text-[10px] text-muted-foreground px-1.5">
              {active} applied
            </span>
            <Button
              size="sm"
              variant="ghost"
              className="h-7 px-2 text-[11px]"
              onClick={onClear}
              data-testid={testid ? `${testid}-clear` : undefined}
            >
              <X className="h-3 w-3 mr-1" /> Clear
            </Button>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}
